import {
  type ADMIN_BROADCAST_REASONS,
  BROADCAST_BODY_MAX,
  BROADCAST_CREATE_CONFIRMATION,
  BROADCAST_TITLE_MAX,
  type AdminBroadcastCreateRequest,
  type AdminBroadcastImage,
} from '@steado/admin-contract/broadcasts';

export interface BroadcastFormValues {
  readonly title: string;
  readonly body: string;
  readonly reason: (typeof ADMIN_BROADCAST_REASONS)[number];
  /** datetime-local value; empty string means send now. */
  readonly scheduledAtLocal: string;
  /** Set once an upload succeeds; announcements are the only push with an image. */
  readonly image: AdminBroadcastImage | null;
}

export const EMPTY_BROADCAST_FORM: BroadcastFormValues = {
  title: '',
  body: '',
  reason: 'PRODUCT_ANNOUNCEMENT',
  scheduledAtLocal: '',
  image: null,
};

/** Stated to the operator rather than left to a disabled button. */
export function formIssues(values: BroadcastFormValues): string[] {
  const issues: string[] = [];
  if (values.title.trim().length === 0) issues.push('A title is required.');
  if (values.title.length > BROADCAST_TITLE_MAX) {
    issues.push(`The title is over ${String(BROADCAST_TITLE_MAX)} characters.`);
  }
  if (values.body.trim().length === 0) issues.push('A body is required.');
  if (values.body.length > BROADCAST_BODY_MAX) {
    issues.push(`The body is over ${String(BROADCAST_BODY_MAX)} characters.`);
  }
  if (values.scheduledAtLocal !== '') {
    const scheduled = new Date(values.scheduledAtLocal);
    if (Number.isNaN(scheduled.getTime())) {
      issues.push('The scheduled time is not a valid date.');
    } else if (scheduled.getTime() <= Date.now()) {
      issues.push('The scheduled time must be in the future; clear it to send now.');
    }
  }
  return issues;
}

export function toCreateRequest(values: BroadcastFormValues): AdminBroadcastCreateRequest {
  return {
    title: values.title.trim(),
    body: values.body.trim(),
    reason: values.reason,
    confirmation: BROADCAST_CREATE_CONFIRMATION,
    ...(values.scheduledAtLocal === ''
      ? {}
      : { scheduledAt: new Date(values.scheduledAtLocal).toISOString() }),
    ...(values.image === null ? {} : { imageKey: values.image.key }),
  };
}
